import { z } from 'zod';
import { chilldocToMarkdown } from './chilldoc-markdown.js';
import { ChilldocListStyles } from './chilldoc-lists.js';
import { imageBlocks, readImageBlocks, usedImages, } from './chilldoc-images.js';
import { ChilldocPresentation, slideBreakMarkdown } from './slides.js';
import { ChilldocComments } from './chilldoc-comments.js';
export * from './chilldoc-comments.js';
export * from './chilldoc-images.js';
export * from './chilldoc-lists.js';
/**
 * Định dạng tài liệu `.chilldoc` — APM-CD-a, docs/plan-chilldoc.md §2.
 *
 * Dữ liệu là một **vỏ** (định dạng, phiên bản, lề trang, font Google đã thêm)
 * bọc `doc` là **đúng** `editor.getJSON()` của TipTap. Vỏ kiểm ở đây; `doc` do
 * schema của editor kiểm lúc mở (`schema.nodeFromJSON`), node lạ bị loại.
 *
 * Trên đĩa (người dùng chốt 2026-09-27 — LLM phải đọc dễ như `.md`):
 *
 * ```
 * ---
 * format: chilldoc
 * version: 1
 * ---
 * <markdown sinh từ doc — chỉ để đọc>
 *
 * <!-- chilldoc-data
 * {vỏ JSON — nguồn sự thật}
 * -->
 *
 * <!-- chilldoc-image i… image/webp
 * <base64 — ảnh nhúng, v2, docs/plan-chilldoc-images.md §2.1>
 * -->
 * ```
 */
export const CHILLDOC_FORMAT = 'chilldoc';
/**
 * Phiên bản cao nhất app này đọc được. Gặp bản lớn hơn ⇒ không mở để sửa.
 *
 * 2 = có khối ảnh nhúng (APM-CDIMG-a). Tệp **ghi** ra mang phiên bản thấp nhất
 * đủ cho nội dung của nó (`writeChilldoc`): tệp không ảnh vẫn là 1, nên tab
 * đang mở bản app cũ còn sửa được; tệp có ảnh là 2, nên bản cũ — thứ không biết
 * khối ảnh và sẽ lưu đè mất chúng — chỉ đọc.
 *
 * 3 = có bình luận (APM-CMT-a): bản cũ không biết mark `comment` và `comments`
 * của vỏ, mở rồi lưu là mất bình luận — nên nó chỉ đọc.
 */
export const CHILLDOC_VERSION = 3;
/** Trần một tệp `.chilldoc` — người dùng chốt 2026-09-29 (ảnh nhúng làm tệp lớn lên). */
export const CHILLDOC_MAX_BYTES = 50 * 1024 * 1024;
export const CHILLDOC_EXTENSION = '.chilldoc';
export const CHILLDOC_MIME = 'application/vnd.chillpm.doc+json';
/**
 * Lề mặc định mỗi bên (mm) cho cả ba loại tài liệu. Lề của người đọc/soạn
 * (APM-MYF-h) là mặc định; `.chilldoc` đặt lề riêng ở `settings.margins`
 * (APM-SHR-a, người dùng chốt 2026-09-29).
 */
export const CHILLDOC_DEFAULT_MARGIN_MM = 25;
export const CHILLDOC_MAX_MARGIN_MM = 60;
/** Font Google mỗi tài liệu — đủ cho mọi tài liệu thật, chặn vỏ phình vô hạn. */
export const CHILLDOC_MAX_FONTS = 20;
/** Kẹp thay vì từ chối: tệp chép tay số rác vẫn mở được (plan §2.1). */
const margin = z
    .number()
    .catch(CHILLDOC_DEFAULT_MARGIN_MM)
    .transform((value) => Math.min(CHILLDOC_MAX_MARGIN_MM, Math.max(0, Math.round(Number.isFinite(value) ? value : 0))));
export const ChilldocPage = z.object({
    marginLeft: margin.default(CHILLDOC_DEFAULT_MARGIN_MM),
    marginRight: margin.default(CHILLDOC_DEFAULT_MARGIN_MM),
});
/** Cấp tiêu đề có kiểu riêng trong Cấu hình tài liệu (APM-SHR-a). */
export const CHILLDOC_HEADING_LEVELS = [1, 2, 3, 4];
export const CHILLDOC_MIN_FONT_PX = 8;
export const CHILLDOC_MAX_FONT_PX = 96;
/** Mỗi trường tự bỏ khi hỏng — một màu gõ tay sai không làm mất cả cấu hình. */
const fontPx = z
    .number()
    .int()
    .min(CHILLDOC_MIN_FONT_PX)
    .max(CHILLDOC_MAX_FONT_PX)
    .optional()
    .catch(undefined);
const flag = z.boolean().optional().catch(undefined);
export const ChilldocHeadingStyle = z.object({
    size: fontPx,
    /** `#rrggbb` — đi thẳng vào `style`, nên không nhận chuỗi CSS tự do. */
    color: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .optional()
        .catch(undefined),
    bold: flag,
    italic: flag,
    underline: flag,
    /** Tên font trong `fonts` của tài liệu. */
    font: z.string().trim().min(1).max(60).optional().catch(undefined),
});
const headingStyle = ChilldocHeadingStyle.optional().catch(undefined);
/**
 * Cấu hình **của tài liệu** (APM-SHR-a, người dùng chốt 2026-09-29): ai mở tệp —
 * kể cả người nhận link — thấy cùng một kiểu. Trường vắng ⇒ như trước khi có
 * cấu hình (CSS mặc định, lề của người đang xem).
 */
export const ChilldocSettings = z.object({
    /** Cỡ chữ của đoạn văn (px). */
    baseSize: fontPx,
    headings: z
        .object({ h1: headingStyle, h2: headingStyle, h3: headingStyle, h4: headingStyle })
        .optional()
        .catch(undefined),
    /**
     * Lề trái/phải (mm). Trước đây lề là tuỳ chọn của người xem (APM-MYF-h); từ
     * APM-SHR-a tài liệu mang lề của nó — tệp chưa đặt thì vẫn theo người xem.
     */
    margins: z.object({ left: margin, right: margin }).optional().catch(undefined),
    /** Kiểu đánh số / gạch đầu dòng theo cấp (APM-SHR-f, `chilldoc-lists.ts`). Rác ⇒ như không có. */
    lists: ChilldocListStyles.optional().catch(undefined),
    /**
     * Theo dõi thay đổi (APM-CD2-h, `chilldoc-suggest.ts`): bật ⇒ gõ / xoá thành đề xuất cho
     * người khác chấp nhận / từ chối. Vắng ⇒ tắt.
     */
    trackChanges: z.boolean().optional().catch(undefined),
});
export const ChilldocExternal = z.object({
    /**
     * `'pptx'` @deprecated APM-PPX-i (plan-pptx-editor §8): `.pptx` sửa bằng hộp soạn PowerPoint,
     * không còn mở thành chilldoc nên không ai ghi giá trị này nữa — giữ để tệp/nháp cũ còn đọc
     * được. Trường `external` thì **không** bỏ: Word vẫn ghi nó lúc mở `.docx`.
     */
    kind: z.enum(['word', 'pptx']),
    fileId: z.string().trim().min(1).max(64),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export const ChilldocEnvelope = z.object({
    format: z.literal(CHILLDOC_FORMAT),
    version: z.number().int().min(1),
    /**
     * ⚠️ Bỏ dùng: lề lưu theo người dùng (APM-MYF-h), không theo tệp. Vẫn nhận để
     * tệp của bản đầu `-a` mở được; không ghi nữa.
     */
    page: ChilldocPage.optional().catch(undefined),
    fonts: z.array(z.string().trim().min(1).max(60)).max(CHILLDOC_MAX_FONTS).catch([]).default([]),
    /** Tỉ lệ + chủ đề của bộ slide (APM-SLD-a). Không có ⇒ 16:9, Sáng; rác ⇒ như không có. */
    presentation: ChilldocPresentation.optional().catch(undefined),
    /** Cấu hình tài liệu (APM-SHR-a). Rác ⇒ như không có. */
    settings: ChilldocSettings.optional().catch(undefined),
    /** Bình luận (APM-CMT-a, `chilldoc-comments.ts`). Không có ⇒ tài liệu chưa có bình luận. */
    comments: ChilldocComments.optional(),
    /**
     * Tệp Office gốc mà tài liệu này dựng từ đó (APM-PPE-c, plan-office-editors §8.2):
     * phiên bản đang sửa và băm byte của nó. Lúc Lưu, bộ vá tải lại tệp ấy và chỉ ghi
     * phần đã đổi; băm lệch ⇒ gốc đã bị thay, rơi về dựng lại. Nháp tự lưu mang theo.
     *
     * Từ APM-PPX-i chỉ Word (`kind: 'word'`) còn ghi trường này — xem `ChilldocExternal.kind`.
     */
    external: ChilldocExternal.optional().catch(undefined),
    /** ProseMirror JSON (`{ type: 'doc', content: [...] }`) — schema của editor kiểm. */
    doc: z.object({ type: z.literal('doc') }).loose(),
});
export function isChilldocName(filename) {
    return filename.toLowerCase().endsWith(CHILLDOC_EXTENSION);
}
/**
 * Ba loại tài liệu soạn được ở trang Tệp (người dùng chốt 2026-09-27): văn bản
 * thuần (chỉ Lưu/Đổi tên/Đóng), markdown (nút markdown hỗ trợ), `.chilldoc`
 * (đầy đủ). Đuôi quyết định loại — một nguồn sự thật cho API (`canEdit`,
 * `revise`) và trình soạn.
 */
export const DOCUMENT_FORMATS = ['text', 'markdown', 'chilldoc'];
export const DOCUMENT_EXTENSIONS = {
    text: '.txt',
    markdown: '.md',
    chilldoc: CHILLDOC_EXTENSION,
};
export const DOCUMENT_MIME_TYPES = {
    text: 'text/plain',
    markdown: 'text/markdown',
    chilldoc: CHILLDOC_MIME,
};
/** Loại tài liệu theo đuôi, hoặc `null` nếu trang Tệp không soạn được tệp này. */
export function documentFormatOf(filename) {
    const lower = filename.toLowerCase();
    return DOCUMENT_FORMATS.find((format) => lower.endsWith(DOCUMENT_EXTENSIONS[format])) ?? null;
}
/** `.txt`, `.md` hoặc `.chilldoc` — những tệp trang Tệp mở để sửa được. */
export function isEditableDocumentName(filename) {
    return documentFormatOf(filename) !== null;
}
const DATA_OPEN = '<!-- chilldoc-data';
const DATA_CLOSE = '-->';
/**
 * Nội dung tệp: markdown đọc được ở trên, vỏ JSON trong chú thích HTML ở dưới.
 * `--` trong JSON được viết thành `-\u002d` (vẫn là JSON hợp lệ, đọc ra y nguyên)
 * để không chuỗi nào đóng chú thích sớm.
 */
export function writeChilldoc(envelope, 
/** Kho ảnh nhúng; chỉ ảnh `doc` còn tham chiếu được ghi (plan-chilldoc-images §2.1). */
images = {}) {
    // Cả vỏ, không chỉ `doc`: ảnh trong slide Mở đầu / Kết thúc nằm ở `presentation`.
    const kept = usedImages(envelope, images);
    const hasComments = (envelope.comments?.length ?? 0) > 0;
    const version = hasComments ? 3 : Object.keys(kept).length > 0 ? 2 : 1;
    // `page` không ghi nữa (lề theo người dùng) — tệp cũ mở rồi lưu là rơi mất nó.
    const { page: _page, comments, ...rest } = envelope;
    const stored = { ...rest, ...(hasComments ? { comments } : {}), version };
    const data = JSON.stringify(stored).replace(/--/g, '-\\u002d');
    // Slide của hai vùng ẩn (APM-SLD-k) nằm ở vỏ, không trong `doc` — chữ của chúng vẫn phải tìm được.
    const edge = (slides, label) => slides?.length ? [...slideBreakMarkdown({ slides }, label), ''] : [];
    const markdown = [
        ...edge(envelope.presentation?.opening, 'trình chiếu · mở đầu'),
        chilldocToMarkdown(envelope.doc).trimEnd(),
        ...(envelope.presentation?.closing?.length ? [''] : []),
        ...edge(envelope.presentation?.closing, 'trình chiếu · kết thúc'),
    ].join('\n');
    return [
        '---',
        `format: ${CHILLDOC_FORMAT}`,
        `version: ${version}`,
        '---',
        '',
        markdown.trimEnd(),
        '',
        DATA_OPEN,
        data,
        DATA_CLOSE,
        '',
        ...imageBlocks(kept),
    ].join('\n');
}
/**
 * Phần JSON của tệp: khối `chilldoc-data`, hoặc cả tệp nếu là JSON thuần (bản
 * đầu). `end` = chỗ khối ảnh (v2) bắt đầu — tìm ảnh từ đó, không quét lại chữ.
 */
function dataOf(text) {
    const start = text.lastIndexOf(DATA_OPEN);
    if (start < 0)
        return { json: text, end: text.length };
    const end = text.indexOf(DATA_CLOSE, start + DATA_OPEN.length);
    return end < 0
        ? { json: '', end: text.length }
        : { json: text.slice(start + DATA_OPEN.length, end), end: end + DATA_CLOSE.length };
}
/**
 * Đọc nội dung tệp — **không bao giờ ném**. `newer_version`: tệp do bản app mới
 * hơn ghi; mở rồi lưu đè là mất phần app này không hiểu, nên chỗ gọi chặn sửa.
 */
export function readChilldoc(text) {
    const data = dataOf(text);
    let raw;
    try {
        raw = JSON.parse(data.json);
    }
    catch {
        return { ok: false, reason: 'invalid' };
    }
    const parsed = ChilldocEnvelope.safeParse(raw);
    if (!parsed.success)
        return { ok: false, reason: 'invalid' };
    if (parsed.data.version > CHILLDOC_VERSION)
        return { ok: false, reason: 'newer_version' };
    return { ok: true, envelope: parsed.data, images: readImageBlocks(text, data.end) };
}
/** Vỏ rỗng cho tài liệu mới. */
export function emptyChilldoc() {
    return {
        format: CHILLDOC_FORMAT,
        // Không phải `CHILLDOC_VERSION`: phiên bản ghi ra do `writeChilldoc` chọn theo nội dung.
        version: 1,
        fonts: [],
        doc: { type: 'doc', content: [{ type: 'paragraph' }] },
    };
}
//# sourceMappingURL=chilldoc.js.map